import { NgModule } from '@angular/core';
import { Route, RouterModule } from '@angular/router';

import {
  AgentDirectoryPageRoute,
  ListingDirectoryPageRoute,
  NeighbourhoodDirectoryPageRoute,
  OpenHouseDirectoryPageRoute
} from './app-config.constants';

import {
  agentProfileMatcher,
  cityMatcher,
  listingDetailsMatcher,
  officeProfileMatcher,
  propTypeMatcher,
  provinceMatcher,
  popularAgentCityMatcher,
  openHouseMatcher,
  offMarketListingDetailsMatcher,
  businessOpportunitiesCityMatcher,
  sitemapMatcher,
  sitemapRentalMatcher
} from './classes/complex-url-matcher';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { RedirectGuard } from './guards/redirect.guard';

export const routes: Route[] = [
  // -- Complex Matched Routes --
  {
    // Ex: commercial/on/toronto-business-opportunities
    matcher: businessOpportunitiesCityMatcher,
    loadChildren: './routing/business-opportunities-city-routing.module#BusinessOpportunitiesCityRoutingModule',
  },
  {
    // Ex: /on/toronto-open-houses
    matcher: openHouseMatcher,
    loadChildren: './routing/open-house-routing.module#OpenHouseRoutingModule',
  },
  {
    // Ex: /on/toronto-real-estate-agents
    matcher: popularAgentCityMatcher,
    loadChildren: './routing/popular-agent-city-routing.module#PopularAgentCityRoutingModule',
  },
  {
    // Ex: /on/toronto-land-for-sale/
    matcher: propTypeMatcher,
    loadChildren: './routing/prop-type-routing.module#PropTypeRoutingModule',
  },
  {
    // Ex: /on/real-estate-listings-canada
    matcher: sitemapMatcher,
    loadChildren: './routing/sitemap-routing.module#SitemapRoutingModule',
  },
  {
    // Ex: /on/real-estate-rentals-canada
    matcher: sitemapRentalMatcher,
    loadChildren: './routing/sitemap-rental-routing.module#SitemapRentalRoutingModule',
  },
  // Listing/Agent/Office Links for optimized SEO purposes
  {
    // Ex: /on/hoda-abu-shabab-p101354257-ag
    matcher: agentProfileMatcher,
    loadChildren: './routing/agent-profile-routing.module#AgentProfileRoutingModule',
  },
  {
    // Ex: /on/re-max-realtron-realty-inc-643-br
    matcher: officeProfileMatcher,
    loadChildren: './routing/office-profile-routing.module#OfficeProfileRoutingModule',
  },
  {
    // Ex: /on/toronto-real-estate/3155-eglinton-ave-e-1-wp_id197312002-lst
    matcher: listingDetailsMatcher,
    loadChildren: './routing/listing-details-routing.module#ListingDetailsRoutingModule',
  },
  {
    // Ex: /on/toronto-real-estate/3155-eglinton-ave-e-1-wp_id197312002-prty
    matcher: offMarketListingDetailsMatcher,
    loadChildren: './routing/off-market-listing-details-routing.module#OffMarketListingDetailsRoutingModule',
  },
  // End of Listing/Agent/Office Links
  // Province & City Pages
  {
    // Ex: /on/toronto-real-estate
    matcher: cityMatcher,
    loadChildren: './routing/city-routing.module#CityRoutingModule',
  },
  {
    // Ex: /on
    matcher: provinceMatcher,
    loadChildren: './routing/province-routing.module#ProvinceRoutingModule',
  },
  // End of Province & City Pages
  // -- End Complex Matched Routes --

  {
    path: AgentDirectoryPageRoute,
    loadChildren: './routing/agent-directory-routing.module#AgentDirectoryRoutingModule',
  },
  {
    path: 'agents',
    loadChildren: './routing/agents-routing.module#AgentsRoutingModule',
  },
  {
    path: 'commercial',
    loadChildren: './routing/commercial-routing.module#CommercialRoutingModule',
  },
  {
    path: 'commercialagents',
    loadChildren: './routing/commercial-agents-routing.module#CommercialAgentsRoutingModule',
  },
  {
    path: 'commerciallistings',
    loadChildren: './routing/commercial-listings-routing.module#CommercialListingsRoutingModule',
  },
  {
    path: 'find-an-agent',
    loadChildren: './routing/find-an-agent-routing.module#FindAnAgentModule',
  },
  {
    path: 'find-agent-or-office',
    loadChildren: './routing/find-agent-or-office-routing.module#FindAgentOrOfficeRoutingModule',
  },
  {
    path: 'find-real-estate',
    loadChildren: './routing/find-real-estate-routing.module#FindRealEstateRoutingModule',
  },
  {
    path: 'home-estimates',
    loadChildren: './routing/home-estimates-routing.module#HomeEstimatesRoutingModule',
  },
  {
    path: 'home-estimates-canada',
    loadChildren: './routing/home-estimates-routing.module#HomeEstimatesRoutingModule',
  },
  {
    path: ListingDirectoryPageRoute,
    loadChildren: './routing/listing-directory-routing.module#ListingDirectoryRoutingModule',
  },
  {
    path: 'listings',
    loadChildren: './routing/listings-routing.module#ListingsRoutingModule',
  },
  {
    path: 'login',
    redirectTo: 'my-remax',
  },
  {
    path: 'luxury',
    loadChildren: './routing/luxury-routing.module#LuxuryRoutingModule',
  },
  {
    path: 'luxuryagents',
    loadChildren: './routing/luxury-agents-routing.module#LuxuryAgentsRoutingModule',
  },
  {
    path: 'luxurylistings',
    loadChildren: './routing/luxury-listings-routing.module#LuxuryListingsRoutingModule',
  },
  {
    path: 'manage-notifications',
    loadChildren: './routing/manage-notifications-routing.module#ManageNotificationsRoutingModule',
  },
  {
    path: 'my-account',
    pathMatch: 'full',
    canActivate: [RedirectGuard],
    component: PageNotFoundComponent, // should never activate but Angular requires a component here
    data: {
      externalRedirect: '/my-remax'
    }
  },
  {
    path: 'my-remax',
    loadChildren: './routing/my-remax-routing.module#MyRemaxRoutingModule',
  },
  {
    path: NeighbourhoodDirectoryPageRoute,
    loadChildren: './routing/neighbourhood-directory-routing.module#NeighbourhoodDirectoryRoutingModule',
  },
  {
    path: 'neighbourhoods',
    loadChildren: './neighbourhoods/neighbourhoods.module#NeighbourhoodsModule',
  },
  {
    path: OpenHouseDirectoryPageRoute,
    loadChildren: './routing/open-house-directory-routing.module#OpenHouseDirectoryRoutingModule',
  },
  {
    path: 'persona/*',
    redirectTo: '404',
  },
  {
    path: 'print-listing',
    loadChildren: './routing/print-listing-routing.module#PrintListingRoutingModule',
  },
  {
    path: 'render-placeholder',
    loadChildren: './routing/render-placeholder-routing.module#RenderPlaceholderRoutingModule',
  },
  {
    path: 'reset-password',
    loadChildren: './routing/reset-password-routing.module#ResetPasswordRoutingModule',
  },
  {
    path: 'unsubscribe',
    loadChildren: './routing/unsubscribe-routing.module#UnsubscribeRoutingModule',
  },
  {
    path: '',
    loadChildren: './routing/home-routing.module#HomeRoutingModule'
  },
  {
    path: '404',
    loadChildren: './routing/page-not-found-routing.module#PageNotFoundRoutingModule'
  },
  {
    path: 'firsthomecontest',
    pathMatch: 'full',
    canActivate: [RedirectGuard],
    component: PageNotFoundComponent, // should never activate but Angular requires a component here
    data: {
      externalRedirect: 'https://blog.remax.ca/firsthomecontest/'
    }
  },
  {
    path: '**',
    redirectTo: '/404'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    initialNavigation: 'enabled',
    onSameUrlNavigation: 'reload'
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
